import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BadgePercent, Gift, Package, ShoppingBag, TrendingUp, Wallet } from "lucide-react";
import { toast } from "sonner";

import Cabecalho from "../comum/Cabecalho";
import Carregando from "../comum/Carregando";
import FiltroPeriodo from "../comum/FiltroPeriodo";
import GraficoDias from "../comum/GraficoDias";
import Kpi from "../comum/Kpi";
import SemanaHora from "../comum/SemanaHora";
import Selo from "../comum/Selo";
import { num, pct, qtdTotal, qtdVendida, reais } from "../comum/formato";
import { useFiltros } from "../comum/useFiltros";
import { ROTA_PROMOCOES } from "../rotas";
import { carregarPromocao } from "./api";
import Avisos from "./Avisos";
import PedidosDaPromocao from "./PedidosDaPromocao";
import ProdutosPromocao from "./ProdutosPromocao";
import RegrasPromocao from "./RegrasPromocao";

// Relatório de uma promoção (Relatório de Promoções → clique na promoção).
export default function PromocaoDetalhePage() {
  const { chave } = useParams();
  const { filtros, mudar, query, paraApi } = useFiltros();
  const [rel, setRel] = useState(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    let vivo = true;
    // Trocar período/filtro mantém o relatório na tela até o novo chegar (25/10/2026)
    setErro(false);
    carregarPromocao(decodeURIComponent(chave), { date_from: paraApi.date_from, date_to: paraApi.date_to, modulo: paraApi.modulo })
      .then((d) => { if (vivo) setRel(d); })
      .catch(() => { if (vivo) { setErro(true); toast.error("Não foi possível carregar a promoção"); } });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave, paraApi.date_from, paraApi.date_to, paraApi.modulo]);

  const p = rel?.promocao;
  const r = rel?.resumo || {};
  const a = rel?.anterior || {};
  const abaixo = (rel?.produtos || []).filter((x) => x.lucro_unit != null && x.lucro_unit < 0);
  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-6xl mx-auto space-y-5" data-e2e="detalhe-promocao">
        <Cabecalho voltarPara={`${ROTA_PROMOCOES}${query({ busca: "" })}`} voltarRotulo="Relatório de Promoções" icone={Gift} corIcone="bg-emerald-500/10 text-emerald-500"
          titulo={!rel && !erro ? <span className="inline-block h-7 w-48 max-w-full align-middle animate-pulse rounded-md bg-primary/10" aria-label="Carregando" /> : p?.nome || "Promoção"}
          subtitulo={p && <span className="flex flex-wrap items-center gap-1.5"><Selo status={p.status} rotulo={p.status_rotulo} /><span>{p.tipo_rotulo}</span>{p.descricao && p.tipo !== "nao_identificada" ? <span>· {p.descricao}</span> : null}</span>} />
        <FiltroPeriodo de={filtros.de} ate={filtros.ate} onMudar={mudar} cor="bg-emerald-500 text-white" />

        {erro ? <p className="text-sm text-muted-foreground">Promoção não encontrada.</p> : !rel ? <Carregando /> : (
          <>
            <RegrasPromocao promocao={p} />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Kpi rotulo="Quantidade vendida" valor={qtdVendida(r)} atual={qtdTotal(r)} anterior={qtdTotal(a)} dica={`${r.pedidos} pedidos`} icone={Package} e2e="kpi-promo-unidades" />
              <Kpi rotulo="Faturado" valor={reais(r.faturado)} atual={r.faturado} anterior={a.faturado} icone={Wallet} destaque="bg-emerald-500/10 border-emerald-500/30" />
              <Kpi rotulo="Desconto concedido" valor={reais(r.desconto)} atual={r.desconto} anterior={a.desconto} menorMelhor icone={BadgePercent} />
              <Kpi rotulo="Lucro (ficha técnica)" valor={r.lucro == null ? "—" : reais(r.lucro)} atual={r.lucro} anterior={a.lucro} dica={r.lucro == null ? "sem ficha técnica" : `${pct(r.margem_pct)} margem`} icone={TrendingUp} e2e="kpi-promo-lucro" />
              <Kpi rotulo="Retorno do desconto" valor={r.retorno ? reais(r.retorno) : "—"} atual={r.retorno} anterior={a.retorno} dica="em vendas por R$ 1" icone={TrendingUp} />
              <Kpi rotulo="Levaram junto" valor={reais(r.vendas_acompanhantes)} atual={r.vendas_acompanhantes} anterior={a.vendas_acompanhantes} dica="em outros produtos" icone={ShoppingBag} />
              <Kpi rotulo="Ticket dos pedidos" valor={reais(r.ticket_pedidos)} atual={r.ticket_pedidos} anterior={a.ticket_pedidos} icone={Wallet} />
              <Kpi rotulo="Clientes" valor={r.clientes} atual={r.clientes} anterior={a.clientes} dica={r.cancelados ? `${r.cancelados} cancelados` : null} icone={ShoppingBag} />
            </div>
            <Avisos resumo={r} abaixoDoCusto={abaixo} />
            <ProdutosPromocao produtos={rel.produtos} titulo="Produto a produto" />
            <div className="grid lg:grid-cols-5 gap-4">
              <div className="lg:col-span-3">
                <GraficoDias serie={rel.por_dia} principal="faturado" secundaria="desconto" titulo="Vendas por dia" corPrincipal="bg-emerald-500"
                  legenda={[{ rotulo: "Faturado", cor: "bg-emerald-500" }, { rotulo: "Desconto", cor: "bg-amber-400" }]} />
              </div>
              <div className="lg:col-span-2"><SemanaHora semana={rel.semana} hora={rel.hora} titulo="Quando vende" /></div>
            </div>
            <PedidosDaPromocao pedidos={rel.pedidos} />
          </>
        )}
      </div>
    </div>
  );
}
